<template>
  <div ref="reactRoot" />
</template>

<script lang="ts">
function getAssetUrl(localPath: string, cdnUrl: string): string {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    try {
      const req = new XMLHttpRequest()
      req.open('GET', localPath, false)
      req.send()
      const isHtml = req.responseText?.trimStart().toLowerCase().startsWith('<!doctype html>')
      if (req.status >= 200 && req.status < 400 && req.responseText && !isHtml) {
        return localPath
      }
    } catch {
      // fallback to CDN
    }
  }
  return cdnUrl
}
</script>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

export interface Props {
  jsUrl?: string
  cssUrl?: string
  initialize: (n?: HTMLElement | null) => void
}

const props = withDefaults(defineProps<Props>(), {
  jsUrl: getAssetUrl(
    '/pitcher-knock.umd.js',
    'https://cdn.jsdelivr.net/npm/@pitcher/ng-knocktifications@2025.10.9-85405/dist/pitcher-knock.umd.js',
  ),
  cssUrl: getAssetUrl(
    '/pitcher-knock.css',
    'https://cdn.jsdelivr.net/npm/@pitcher/ng-knocktifications@2025.10.9-85405/dist/pitcher-knock.css',
  ),
})
const reactRoot = ref<HTMLElement | null>(null)

onMounted(async () => {
  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve()
        return
      }
      const script = document.createElement('script')
      script.src = src
      script.className = 'added-by-knock-vue-wrapper'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
      document.head.appendChild(script)
    })
  }

  const loadStyle = (href: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve()
        return
      }
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.onload = () => resolve()
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`))
      document.head.appendChild(link)
    })
  }

  // import React if not there already -- order is important
  if (!window.React) await loadScript('https://cdn.jsdelivr.net/npm/react@18/umd/react.production.min.js')
  if (!(window as any).ReactDOM)
    await loadScript('https://cdn.jsdelivr.net/npm/react-dom@18/umd/react-dom.production.min.js')

  await Promise.all([loadStyle(props.cssUrl), loadScript(props.jsUrl)])

  // Ensure props.run receives a DOM element
  props.initialize?.(reactRoot.value)
})
</script>
